import React, { useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FiClock, FiHeart, FiInfo, FiCheckCircle } from "react-icons/fi";

// --- Data ---
const LASH_PRICES = [
  {
    category: "Einzeltechnik",
    items: [
      { name: "Neumodellage", price: "€ 100" },
      { name: "Auffüllen bis 2 Wochen", price: "€ 40" },
      { name: "Auffüllen bis 3 Wochen", price: "€ 50" },
      { name: "Auffüllen bis 4 Wochen", price: "€ 60" },
    ],
  },
  {
    category: "Wispy Technik",
    items: [
      { name: "Neumodellage Soft Wispy", price: "€ 115" },
      { name: "Auffüllen bis 4 Wochen", price: "€ 65" },
      { name: "Neumodellage Mega Wispy", price: "€ 130" },
      { name: "Auffüllen bis 4 Wochen", price: "€ 70" },
    ],
  },
  {
    category: "Volumentechnik",
    items: [
      { name: "Neumodellage", price: "€ 130" },
      { name: "Auffüllen bis 2 Wochen", price: "€ 50" },
      { name: "Auffüllen bis 3 Wochen", price: "€ 60" },
      { name: "Auffüllen bis 4 Wochen", price: "€ 65" },
    ],
  },
  {
    category: "Lifting",
    items: [
      { name: "Wimpernlaminierung mit Färben", price: "€ 60 / € 65" },
      { name: "Augenbrauenlifting mit Formen & Färben", price: "€ 55 / € 65" },
    ],
  },
];

const PMU_PRICES = [
  {
    category: "Augenbrauen",
    description: "Permanent Make-up von Simona",
    items: [
      {
        name: "Erstbehandlung",
        sub: "Für alle Hauttypen geeignet",
        price: "€ 290",
      },
      { name: "Nachbehandlung", sub: "Bis 2 Monate", price: "€ 110" },
      { name: "Nachbehandlung", sub: "Bis 2 Jahre", price: "€ 160" },
      { name: "Auffrischung", sub: "Ab 2-3 Jahre", price: "€ 190" },
    ],
  },
];

// --- Styled Components ---
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #fcf9f7 0%, #f5f0eb 100%);
  padding: 100px 20px;

  @media (max-width: 500px) {
    padding: 80px 12px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 70px;

  @media (max-width: 500px) {
    margin-bottom: 50px;
  }
`;

const Title = styled.h1`
  font-family: "Lovelace", serif;
  font-size: clamp(3rem, 6vw, 5rem);
  color: #63564e;
  margin: 0 0 20px;
  font-weight: 400;
  letter-spacing: -1px;

  @media (max-width: 500px) {
    font-size: 2.5rem;
    margin-bottom: 15px;
  }
`;

const Subtitle = styled.p`
  font-family: "Cormorant Garamond", serif;
  font-size: 1.3rem;
  color: #8e7479;
  font-style: italic;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: 500px) {
    font-size: 1.1rem;
    padding: 0 15px;
  }
`;

const Section = styled.section`
  margin-bottom: 80px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 500px) {
    margin-bottom: 60px;
  }
`;

const SectionHeader = styled(motion.div)`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(155, 128, 103, 0.2);

  h2 {
    font-family: "Lovelace", serif;
    font-size: 2.2rem;
    color: #63564e;
    margin: 0;
    font-weight: 400;
  }

  span {
    color: #9b8067;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 2px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }

  @media (max-width: 500px) {
    margin-bottom: 20px;
    padding-bottom: 12px;

    h2 {
      font-size: 1.8rem;
    }

    span {
      font-size: 0.8rem;
    }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 25px;
  }

  @media (max-width: 500px) {
    gap: 20px;
  }
`;

const PriceCard = styled(motion.div)`
  background: white;
  border-radius: 30px;
  padding: 35px;
  box-shadow: 0 20px 40px rgba(99, 86, 78, 0.05);
  border: 1px solid rgba(155, 128, 103, 0.1);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 30px 60px rgba(99, 86, 78, 0.1);
    transform: translateY(-5px);
  }

  @media (max-width: 500px) {
    padding: 25px 20px;
    border-radius: 25px;
  }
`;

const CardCategory = styled.h3`
  font-family: "Lovelace", serif;
  font-size: 1.6rem;
  color: #63564e;
  margin: 0 0 25px;
  font-weight: 400;
  padding-bottom: 12px;
  border-bottom: 2px solid rgba(155, 128, 103, 0.2);

  @media (max-width: 500px) {
    font-size: 1.4rem;
    margin-bottom: 20px;
    padding-bottom: 10px;
  }
`;

const PriceItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 12px 0;
  border-bottom: 1px dashed rgba(155, 128, 103, 0.15);

  &:last-child {
    border-bottom: none;
  }

  .info {
    flex: 1;

    .name {
      font-family: "Montserrat", sans-serif;
      font-size: 0.95rem;
      color: #4a3a3d;
      margin-bottom: 4px;
      font-weight: 500;
      line-height: 1.4;
    }

    .sub {
      font-size: 0.75rem;
      color: #8e7479;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  .price {
    font-family: "Lovelace", serif;
    font-size: 1.3rem;
    color: #9b8067;
    font-weight: 400;
    white-space: nowrap;
    margin-left: 15px;
  }

  @media (max-width: 500px) {
    padding: 10px 0;

    .info .name {
      font-size: 0.85rem;
      line-height: 1.3;
    }

    .info .sub {
      font-size: 0.7rem;
    }

    .price {
      font-size: 1.1rem;
      margin-left: 10px;
    }
  }
`;

const InfoBox = styled(motion.div)`
  background: #9b8067;
  color: white;
  border-radius: 30px;
  padding: 50px;
  margin-top: 50px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 40px;
    gap: 30px;
    margin-top: 40px;
  }

  @media (max-width: 500px) {
    padding: 30px 20px;
    border-radius: 25px;
    margin-top: 30px;
    gap: 25px;
  }
`;

const InfoContent = styled.div`
  h3 {
    font-family: "Lovelace", serif;
    font-size: 2rem;
    margin: 0 0 20px;
    font-weight: 400;
    line-height: 1.2;

    @media (max-width: 500px) {
      font-size: 1.6rem;
      margin-bottom: 15px;
    }
  }

  p {
    font-family: "Montserrat", sans-serif;
    font-size: 0.95rem;
    line-height: 1.7;
    opacity: 0.9;
    margin-bottom: 20px;

    @media (max-width: 500px) {
      font-size: 0.85rem;
      line-height: 1.6;
      margin-bottom: 15px;
    }
  }
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  svg {
    font-size: 1.1rem;
    opacity: 0.9;
    flex-shrink: 0;
  }

  span {
    font-size: 0.9rem;
    line-height: 1.4;
  }

  @media (max-width: 500px) {
    gap: 10px;

    svg {
      font-size: 1rem;
    }

    span {
      font-size: 0.8rem;
    }
  }
`;

const InfoStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;

  @media (max-width: 500px) {
    gap: 20px;
  }
`;

const Stat = styled.div`
  text-align: center;

  .number {
    font-family: "Lovelace", serif;
    font-size: 2.2rem;
    font-weight: 400;
    margin-bottom: 5px;

    @media (max-width: 500px) {
      font-size: 1.8rem;
    }
  }

  .label {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.8;

    @media (max-width: 500px) {
      font-size: 0.7rem;
    }
  }
`;

const ExtraService = styled(motion.div)`
  margin-top: 40px;
  padding: 20px 30px;
  background: white;
  border-radius: 60px;
  display: inline-flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 10px 30px rgba(155, 128, 103, 0.1);
  border: 1px solid rgba(155, 128, 103, 0.1);

  svg {
    color: #9b8067;
    font-size: 1.4rem;
    flex-shrink: 0;
  }

  span {
    color: #63564e;
    font-size: 1rem;
    font-style: italic;
    line-height: 1.4;
  }

  strong {
    color: #9b8067;
    font-family: "Lovelace", serif;
    font-size: 1.2rem;
    margin-left: 10px;
    white-space: nowrap;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
    width: 100%;
    padding: 20px;
    border-radius: 30px;
  }

  @media (max-width: 500px) {
    margin-top: 30px;
    gap: 12px;

    svg {
      font-size: 1.2rem;
    }

    span {
      font-size: 0.9rem;
    }

    strong {
      font-size: 1.1rem;
      margin-left: 0;
    }
  }
`;

const PMUCard = styled(PriceCard)`
  background: linear-gradient(135deg, #fcf9f7 0%, #fff 100%);
  border: 2px solid rgba(155, 128, 103, 0.15);
  position: relative;
  overflow: hidden;

  &::before {
    content: "PMU";
    position: absolute;
    top: 20px;
    right: 20px;
    font-family: "Lovelace", serif;
    font-size: 0.8rem;
    color: #9b8067;
    opacity: 0.3;
    font-weight: 400;
  }

  @media (max-width: 500px) {
    &::before {
      top: 15px;
      right: 15px;
      font-size: 0.7rem;
    }
  }
`;

// --- Animation Variants ---
const fadeUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const staggerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

// --- Component ---
const Preise = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    document.title = "Preise - By Simi";
  }, []);
  return (
    <PageContainer>
      <ContentWrapper>
        <Header initial="hidden" animate="visible" variants={fadeUpVariants}>
          <Title>Preisliste</Title>
          <Subtitle>
            Transparente Preise für hochwertige Beauty-Behandlungen
          </Subtitle>
        </Header>

        {/* Lash Prices Section */}
        <Section>
          <SectionHeader
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariants}
          >
            <h2>Wimpernverlängerung</h2>
            <span>Lashes</span>
          </SectionHeader>

          <Grid
            as={motion.div}
            variants={staggerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {LASH_PRICES.map((category, idx) => (
              <PriceCard key={idx} variants={fadeUpVariants}>
                <CardCategory>{category.category}</CardCategory>
                {category.items.map((item, i) => (
                  <PriceItem key={i}>
                    <div className="info">
                      <div className="name">{item.name}</div>
                    </div>
                    <div className="price">{item.price}</div>
                  </PriceItem>
                ))}
              </PriceCard>
            ))}
          </Grid>
        </Section>

        {/* PMU Prices Section */}
        <Section>
          <SectionHeader
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariants}
          >
            <h2>Permanent Make-up</h2>
            <span>PMU</span>
          </SectionHeader>

          <Grid
            as={motion.div}
            variants={staggerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {PMU_PRICES.map((category, idx) => (
              <PMUCard key={idx} variants={fadeUpVariants}>
                <CardCategory>{category.category}</CardCategory>
                {category.items.map((item, i) => (
                  <PriceItem key={i}>
                    <div className="info">
                      <div className="name">{item.name}</div>
                      {item.sub && <div className="sub">{item.sub}</div>}
                    </div>
                    <div className="price">{item.price}</div>
                  </PriceItem>
                ))}
              </PMUCard>
            ))}
          </Grid>
        </Section>

        {/* Info Box */}
        <InfoBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUpVariants}
        >
          <InfoContent>
            <h3>Gut zu wissen</h3>
            <p>
              Ein Beratungsgespräch ist bei mir immer kostenfrei. Je nach
              Hauttyp hält das PMU 2-3 Jahre. Ich wende eine natürliche Technik
              an, die nur in die Epidermis gestochen wird.
            </p>
            <Feature>
              <FiCheckCircle />{" "}
              <span>Natürliche Ergebnisse ohne Blaustich</span>
            </Feature>
            <Feature>
              <FiCheckCircle /> <span>Individuelle Farbberatung inklusive</span>
            </Feature>
            <Feature>
              <FiCheckCircle /> <span>Moderne, schonende Techniken</span>
            </Feature>
          </InfoContent>

          <InfoStats>
            <Stat>
              <div className="number">2-3</div>
              <div className="label">Jahre Haltbarkeit</div>
            </Stat>
            <Stat>
              <div className="number">100%</div>
              <div className="label">Natürlich</div>
            </Stat>
            <Stat>
              <div className="number">∞</div>
              <div className="label">Beratung gratis</div>
            </Stat>
            <Stat>
              <div className="number">✨</div>
              <div className="label">Moderne Technik</div>
            </Stat>
          </InfoStats>
        </InfoBox>

        {/* Extra Service */}
        <ExtraService
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUpVariants}
        >
          <FiHeart />
          <span>Fachgerechte Entfernung der Wimpernextensions</span>
          <strong>€ 20</strong>
        </ExtraService>
      </ContentWrapper>
    </PageContainer>
  );
};

export default Preise;
